import React from 'react';
import styled, { css, keyframes } from 'styled-components';
import visual01 from '../../assets/images/visual01.png';
import lalafanLogo from '../../assets/images/lalafan_logo.svg';
import heartIcon from '../../assets/images/heart.png';
import { Reveal, Section, SectionInner } from '../section/common';
import { useScrollReveal } from '../../hooks/useScrollReveal';

const MARQUEE_ITEMS = [
  'HYPE',
  'VIRAL',
  'FUN',
  'Create',
  'VIBE',
  'CLOUT',
  'FANDOM',
  'TREND',
  'MOMENT',
] as const;

const MARQUEE_TEXT = MARQUEE_ITEMS.join('・');

const logoPop = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.55) translateY(20px);
  }
  70% {
    opacity: 1;
    transform: scale(1.02) translateY(-3px);
  }
  100% {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
`;

const marqueeLtr = keyframes`
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
`;

const marqueeRtl = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

const HeroSectionWrapper = styled(Section)`
  padding-top: 224px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-top: 114px;
  }
`;

const HeroInner = styled(SectionInner)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: 100%;
  max-width: 1440px;
  padding-bottom: 80px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding-bottom: 48px;
  }
`;

const LogoBlock = styled.div`
  position: relative;
  width: min(982px, 100%);
  margin-bottom: 28px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 48px;
    margin-bottom: 32px;
  }
`;

const MainLogo = styled.img`
  width: min(982px, 100%);
  object-fit: contain;
  object-position: center;
  transform-origin: center bottom;
  animation: ${logoPop} 1s cubic-bezier(0.22, 1, 0.36, 1) forwards;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 48px;
  }
`;

const HeartDecor = styled.img`
  width: 121px;
  height: 88px;
  position: absolute;
  left: 658px;
  top: -100px;
  animation: heartFloat 2.8s ease-in-out infinite;

  @keyframes heartFloat {
    0%,
    100% {
      transform: translateY(0) rotate(-6deg);
    }
    50% {
      transform: translateY(-8px) rotate(6deg);
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: -46px;
    left: 220px;
    right: 0;
    width: 72px;
    height: 52px;
  }
`;

const TitleGroup = styled.div`
  width: min(982px, 100%);
`;

const HeadlineFirstLine = styled.span`
  display: contents;
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    flex-shrink: 1;
  }
`;

const HeadlineRow = styled.h1`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  flex-wrap: nowrap;
  width: 100%;
  font-size: 32px;
  font-weight: 600;
  line-height: 1.4;
  letter-spacing: 1.2px;
  color: #111111;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 8px;
    font-size: 24px;
    letter-spacing: -0.6px;
  }
`;

const HeadlineText = styled.span`
  color: #111;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 48px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 62.4px */
  // letter-spacing: -1.2px;
  white-space: nowrap;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 24px;
    line-height: 140%;
    letter-spacing: -1.2px;
    white-space: normal;
    flex-shrink: 0;
  }
`;

const HeadlineDivider = styled.span`
  flex: 1 1 auto;
  min-width: 80px;
  max-width: 140px;
  height: 6px;
  flex-shrink: 0;
  background: #111111;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex: 1;
    min-width: 40px;
    max-width: none;
    width: auto;
    height: 3px;
    align-self: center;
    background: #111111;
  }
`;

const Description = styled.p`
  padding: 60px 0;
  color: #555;
  text-align: center;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: 24px;
  font-style: normal;
  font-weight: 500;
  line-height: 140%; /* 33.6px */
  letter-spacing: -0.6px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 32px 0 40px;
    align-self: flex-start;
    text-align: left;
    color: var(--Grey-Grey500, #555);
    font-family: Pretendard;
    font-size: 15px;
    font-style: normal;
    font-weight: 500;
    line-height: 140%; /* 21px */
    letter-spacing: -0.375px;
    br {
      display: none;
    }
  }
`;

const VisualWrap = styled.div`
  position: relative;
  width: 100%;
`;

const VisualBlock = styled.div`
  position: relative;
  width: 100%;
  border-radius: 24px;
  overflow: hidden;
`;

const HeroImage = styled.img`
  display: block;
  width: 100%;
  aspect-ratio: 70 / 37;
  object-fit: contain;
  border-radius: 24px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    height: 330px;
    object-fit: cover;
    aspect-ratio: 67 / 66;
  }
`;

const MarqueeOverlay = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 100vw;
  transform: translateX(-50%);
  overflow: visible;
  pointer-events: none;
  z-index: 2;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: 162px;
  }
`;

const MARQUEE_BAND_WIDTH = '150vw';

const MarqueeStage = styled.div<{
  $top: string;
  $offsetX?: number;
  $zIndex?: number;
}>`
  position: absolute;
  left: 50%;
  width: ${MARQUEE_BAND_WIDTH};
  top: ${({ $top }) => $top};
  z-index: ${({ $zIndex = 1 }) => $zIndex};
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translate(calc(-50% + ${({ $offsetX = 0 }) => $offsetX}px), -50%);
`;

const MarqueeRotate = styled.div<{ $rotate: number }>`
  flex: none;
  width: 100%;
  transform: rotate(${({ $rotate }) => $rotate}deg);
  transform-origin: center center;
`;

const MarqueeBand = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  overflow: hidden;
  background: #111111;
  padding: 20px;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid ${({ theme }) => theme.colors.lime};
    pointer-events: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 12px 20px;
  }
`;

const MarqueeTrack = styled.div<{ $reverse?: boolean; $duration?: number }>`
  display: flex;
  width: max-content;
  animation: ${({ $reverse }) => ($reverse ? marqueeLtr : marqueeRtl)}
    ${({ $duration = 54 }) => $duration}s linear infinite;
  will-change: transform;
`;

const MarqueeText = styled.span<{ $variant: 'filled' | 'outline' }>`
  flex: none;
  white-space: nowrap;
  font-family: 'BBH Bartle', sans-serif;
  font-size: 36px;
  font-weight: 400;
  font-style: normal;
  line-height: 1.2;
  letter-spacing: -0.9px;
  text-transform: none;
  paint-order: stroke fill;

  ${({ $variant, theme }) =>
    $variant === 'outline'
      ? css`
          color: transparent;
          -webkit-text-stroke: 1.5px ${theme.colors.lime};
        `
      : css`
          color: ${theme.colors.lime};
          -webkit-text-stroke: 0;
        `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 16px;
    letter-spacing: -0.4px;

    ${({ $variant, theme }) =>
      $variant === 'outline' &&
      css`
        -webkit-text-stroke-width: 1px;
        -webkit-text-stroke-color: ${theme.colors.lime};
      `}
  }
`;

interface HeroMarqueeStripProps {
  name: 'marquee01' | 'marquee02';
  rotate: number;
  reverse?: boolean;
  top: string;
  offsetX?: number;
  variant: 'filled' | 'outline';
  zIndex?: number;
}

const HeroMarqueeStrip: React.FC<HeroMarqueeStripProps> = ({
  name,
  rotate,
  reverse,
  top,
  offsetX,
  variant,
  zIndex,
}) => {
  const label = `${MARQUEE_TEXT}・${MARQUEE_TEXT}`;

  return (
    <MarqueeStage $top={top} $offsetX={offsetX} $zIndex={zIndex}>
      <MarqueeRotate $rotate={rotate}>
        <MarqueeBand data-name={name}>
          <MarqueeTrack $reverse={reverse}>
            <MarqueeText $variant={variant}>{label}</MarqueeText>
            <MarqueeText $variant={variant} aria-hidden>
              {label}
            </MarqueeText>
          </MarqueeTrack>
        </MarqueeBand>
      </MarqueeRotate>
    </MarqueeStage>
  );
};

const HeroSection: React.FC = () => {
  const { ref, isVisible } = useScrollReveal();

  return (
    <HeroSectionWrapper id="hero">
      <HeroInner ref={ref}>
        <LogoBlock>
          <MainLogo src={lalafanLogo} alt="LALAFAN" />
          <HeartDecor src={heartIcon} alt="" aria-hidden />
        </LogoBlock>

        <Reveal $visible={isVisible}>
          <TitleGroup>
            <HeadlineRow>
              <HeadlineFirstLine>
                <HeadlineText>브랜드에 맞는 인플루언서</HeadlineText>
                <HeadlineDivider aria-hidden />
              </HeadlineFirstLine>
              <HeadlineText>더 빠르고 정확하게</HeadlineText>
            </HeadlineRow>
            <Description>
              팔로워 숫자가 아닌 실제 반응률로 검증된 인플루언서를 연결합니다.
            </Description>
          </TitleGroup>
        </Reveal>

        <VisualWrap>
          <VisualBlock>
            <Reveal $visible={isVisible} $delay={0.2}>
              <HeroImage src={visual01} alt="라라팡 메인 비주얼" />
            </Reveal>
          </VisualBlock>
          <MarqueeOverlay>
            <HeroMarqueeStrip
              name="marquee01"
              rotate={8.12}
              top="62%"
              reverse
              variant="outline"
              zIndex={2}
            />
            <HeroMarqueeStrip
              name="marquee02"
              rotate={-8.13}
              top="78%"
              variant="filled"
              zIndex={1}
            />
          </MarqueeOverlay>
        </VisualWrap>
      </HeroInner>
    </HeroSectionWrapper>
  );
};

export default HeroSection;
